<template>
  <div class="tab-pane fora-pane" :class="{ hidden: !active }">

    <!-- Estado vazio -->
    <div v-if="!foraDeHorario.length" class="atencao-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
      </svg>
      <p>Sem conversas fora de horário</p>
      <small>Conversas com a etiqueta "atendimento fora de horario" aparecerão aqui.</small>
    </div>

    <template v-else>
      <!-- Resumo -->
      <div class="fora-summary">
        <span class="fora-total">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
          {{ foraDeHorario.length }} conversa{{ foraDeHorario.length !== 1 ? 's' : '' }} fora de horário
        </span>
      </div>

      <!-- Grid de cards -->
      <div class="fora-grid">
        <div v-for="c in foraDeHorario" :key="c.id" class="fora-card">

          <!-- Fluxo tag no topo -->
          <div v-if="c.fluxo_id" class="agrd-fluxo-bar">{{ fluxoLabel(c.fluxo_id) }}</div>

          <!-- Cabeçalho: avatar + contato + ID -->
          <div class="fora-card-head">
            <div class="fora-av" :style="{ background: avatarColor(c.contact.name) }">
              <img v-if="c.contact.thumbnail" :src="c.contact.thumbnail" :alt="c.contact.name" style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block;" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
              <span v-else>{{ initials(c.contact.name) }}</span>
            </div>
            <div class="fora-contact">
              <div class="fora-name">{{ c.contact.name }}</div>
              <div v-if="c.contact.phone" class="fora-phone">{{ c.contact.phone }}</div>
            </div>
            <span class="fora-id">#{{ c.id }}</span>
          </div>

          <!-- Corpo: time, agente, status, canal, espera, criação -->
          <div class="fora-card-body">

            <div class="fora-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span :class="{ 'fora-muted': !c.team }">{{ c.team?.name || 'TypeBot' }}</span>
            </div>

            <div class="fora-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span :class="{ 'fora-muted': !c.assignee }">{{ c.assignee?.name || '— sem agente' }}</span>
            </div>

            <div class="fora-pills">
              <span class="status-pill" :class="c.status">
                <span class="sdot"></span>
                {{ statusLabel(c.status) }}
              </span>
              <span class="ch-pill">{{ fmtChannel(c.channel) }}</span>
            </div>

            <div v-if="waitInfo(c).cls !== 'none'" class="fora-wait">
              <span class="wait-badge" :class="waitInfo(c).cls">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                {{ waitInfo(c).label }}
              </span>
            </div>

            <div class="fora-created">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span>{{ fmtDate(c.created_at) }}</span>
            </div>

            <div v-if="c.labels.length" class="fora-labels">
              <span v-for="lbl in c.labels" :key="lbl" class="label-chip">{{ lbl.replace(/-|_/g, ' ') }}</span>
            </div>

          </div>

          <!-- Rodapé: botão de conversa -->
          <div class="fora-card-foot">
            <button class="fora-chat-btn" @click="requestOpenChat(c.id)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Ver Conversa
            </button>
          </div>

        </div>
      </div>
    </template>

  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import type { Conversation } from '@/types/dashboard'
import { avatarColor, initials, fmtWait, fmtChannel, fluxoLabel } from '@/composables/useHelpers'
import { requestOpenChat } from '@/composables/useChatTrigger'

const props = defineProps<{
  conversations: Conversation[]
  active: boolean
}>()

const emit = defineEmits<{ updateBadge: [count: number] }>()

function isForaLabel(lbl: string): boolean {
  return lbl.toLowerCase().replace(/[\s_]+/g, '-') === 'atendimento-fora-de-horario'
}

const foraDeHorario = computed(() =>
  props.conversations.filter(c => c.status !== 'resolved' && c.labels.some(isForaLabel))
)

watchEffect(() => emit('updateBadge', foraDeHorario.value.length))

function statusLabel(s: string): string {
  const map: Record<string, string> = { open: 'Aberta', resolved: 'Resolvida', pending: 'Pendente' }
  return map[s] || s
}

function waitInfo(c: Conversation) {
  return fmtWait(c.waiting_since)
}

function fmtDate(ts: number): string {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

</script>
